import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuraLayout, Layout } from "lola-framework-ui-test";
import { Toast } from "../../components/Toast";
import {
  GALLERY_NOTICE_FIXTURES,
  GalleryInboxChrome,
  GalleryInboxDrawer,
  GalleryPreferencesSheet,
} from "../../components/notifications/GalleryInbox";
import { ConsumerSessionMenu, GALLERY_CONSUMER } from "../../components/consumer-auth/ConsumerSessionMenu";
import { CurrentExchangeRateCard } from "../../components/fund-request/CurrentExchangeRateCard";
import { TransactionsHistory } from "../../components/TransactionsHistory";
import { FUND_REQUEST_ROUTES } from "../../constants";
import {
  fundRequestMockReceipts,
  fundRequestMockTransactions,
} from "../../gallery/fundRequestMock";
import { useGalleryCashDepositLaunch } from "../../hooks/useGalleryCashDepositLaunch";
import { useSessionStore } from "../../store/useSessionStore";
import { useStore } from "../../store/globalState";
import type { ITransaction } from "../../interfaces/transactions";
import { Navbar } from "../../components/Navbar";

/**
 * Fund-request transactions list (gallery).
 *
 * Mock operations include a pending cash-deposit row and completed/processing
 * transfers. Tapping a normal row opens the receipt gallery; tapping a pending
 * deposit row runs the face sweep (`.deposit-wave`) then hands off to cash-deposit.
 *
 * Chrome matches the live widget: no back, no footer, session menu on the left.
 */
interface TransactionsPageProps {
  /** Gallery inspectable: freeze the pending-cash row in face-sweep launch. */
  depositLaunching?: boolean;
  /** Gallery inspectable: open the notifications drawer on first paint. */
  inboxOpen?: boolean;
  /** Gallery inspectable: pin the arrival toast so it can be restyled. */
  toastOpen?: boolean;
  /** Gallery inspectable: open the preferences sheet on first paint. */
  preferencesOpen?: boolean;
}

export const TransactionsPage = memo(({
  depositLaunching = false,
  inboxOpen = false,
  toastOpen = false,
  preferencesOpen: preferencesOpenProp = false,
}: TransactionsPageProps) => {
  const { t } = useTranslation("fund-request");
  const { theme } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { launch: launchCashDeposit, payingOperationId } =
    useGalleryCashDepositLaunch({ frozen: depositLaunching });

  const colorConfig = theme.whiteView;
  const { title, backgroundBtn, textColorBtn } = colorConfig;
  const [noticesOpen, setNoticesOpen] = useState(inboxOpen);
  const [preferencesOpen, setPreferencesOpen] = useState(preferencesOpenProp);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);
  const notices = GALLERY_NOTICE_FIXTURES.filter(
    (item) => !archivedIds.includes(item.id)
  ).map((item) => ({
    id: item.id,
    title: t(item.titleKey),
    body: t(item.bodyKey),
    isRead: item.read || readIds.includes(item.id),
    createdAt: item.createdAt,
  }));

  const userData = session.data?.userData;
  const recipientName =
    [userData?.name?.firstName, userData?.name?.lastName]
      .filter(Boolean)
      .join(" ") ||
    userData?.fullName ||
    t("fundRequest.someone");

  const sessionFirstName =
    GALLERY_CONSUMER.fullName.split(/\s+/).filter(Boolean)[0] ?? "";

  const items = useMemo<ITransaction[]>(
    () => {
      const mapped = fundRequestMockTransactions.map((item) => ({
        ...item,
        recipientName,
        depositName: item.cashDepositPending ? recipientName : undefined,
        launching:
          Boolean(item.cashDepositPending) &&
          (depositLaunching || payingOperationId === item.operationId),
        onDeposit: item.cashDepositPending
          ? () => {
              if (item.operationId) launchCashDeposit(item.operationId);
            }
          : undefined,
        onSelect:
          item.cashDepositPending || !item.operationId
            ? undefined
            : () =>
                navigate(
                  `${FUND_REQUEST_ROUTES.RECEIPT}/${item.operationId}`
                ),
      }));
      if (!depositLaunching) return mapped;
      return [
        ...mapped.filter((item) => item.cashDepositPending),
        ...mapped.filter((item) => !item.cashDepositPending),
      ];
    },
    [
      navigate,
      recipientName,
      launchCashDeposit,
      payingOperationId,
      depositLaunching,
    ]
  );

  const latest = items[0];
  const latestReceipt = latest?.operationId
    ? fundRequestMockReceipts[latest.operationId]
    : undefined;
  const destinationCountry =
    session.data?.config?.defaultCorridor?.destinationCountryCode || "MX";
  const rateCard =
    latest && latestReceipt ? (
      <CurrentExchangeRateCard
        countryIso2={destinationCountry}
        rate={latestReceipt.exchangeRate.toFixed(4)}
        currency={latestReceipt.destinationCurrency}
        label={t("transactions.currentRate")}
      />
    ) : undefined;

  return (
    <>
    <AuraLayout colorConfig={colorConfig} className="need-help-header notifications-home-header">
      <Layout.Header>
        <ConsumerSessionMenu />
        <Navbar
          align="right"
          noBackButton
          icon={
            <GalleryInboxChrome
              ns="fund-request"
              unread={notices.filter((item) => !item.isRead).length}
              bellColor={title}
              badgeBackground={backgroundBtn}
              badgeTextColor={textColorBtn}
              onOpen={() => setNoticesOpen(true)}
            />
          }
        />
      </Layout.Header>
      <Layout.Content className="transactions-section">
        <TransactionsHistory
          items={items}
          title={t("transactions.title")}
          greeting={
            sessionFirstName
              ? t("transactions.greeting", { name: sessionFirstName })
              : undefined
          }
          rateCard={rateCard}
        />
      </Layout.Content>
    </AuraLayout>
    <GalleryInboxDrawer
      ns="fund-request"
      items={notices}
      open={noticesOpen}
      onClose={() => setNoticesOpen(false)}
      onMarkRead={(id) =>
        setReadIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
      }
      onArchive={(id) =>
        setArchivedIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
      }
      onOpenPreferences={() => {
        setNoticesOpen(false);
        setPreferencesOpen(true);
      }}
    />
    <GalleryPreferencesSheet
      ns="fund-request"
      open={preferencesOpen}
      onClose={() => setPreferencesOpen(false)}
    />
    {toastOpen ? (
      <Toast message={t("notifications.exampleToast")} type="info" persist sound />
    ) : null}
  </>
  );
});

export const DepositLaunchingTransactionsPage = memo(() => (
  <TransactionsPage depositLaunching />
));

export const InboxPage = memo(() => <TransactionsPage inboxOpen />);

export const PreferencesPage = memo(() => <TransactionsPage preferencesOpen />);

export const ToastPage = memo(() => <TransactionsPage toastOpen />);
